import { createSelector } from '@reduxjs/toolkit';
import { selectListScene } from './detailSceneSelectors';
import {
  onGetDetailPlugin,
  onGetFormCapturesAttachedToSceneIds,
  onGetSceneIdsAttachedToFormCaptures,
  onGetFormCapturesAttachedToHotspotIds,
  onGetHotspotIdsAttachedToFormCaptures,
  onGetHotspotTypesAttachedToFormCaptures,
  onGetFormCapturesAttachedToHotspotTypes,
} from '../utils';
import { selectDomain, selectIsFreeOwnerPlan } from './mainSelectors';
import {
  DEFAULT_VALUE_CONTROLBAR_CONFIG,
  DEFAULT_VALUE_COPYRIGHT_CONFIG,
  DEFAULT_VALUE_FLOORPLAN_CONFIG,
  DEFAULT_VALUE_LOGO_CONFIG,
  DEFAULT_VALUE_MAP_CONFIG,
  DEFAULT_VALUE_NADIR_CONFIG,
  DEFAULT_VALUE_POPUP_INTRO_CONFIG,
} from '../constants';
import { I_ControlbarKey, I_CopyRightConfig } from '../types/I_Plugin';
import {
  selectDetailProject,
  selectLanguageProjectSelected,
} from './detailProjectSelectors';
import { getCtaValueMultiLang, getTextMultiLang } from '@utils/helper';
import { selectDefaultLanguageProject } from './multiLanguageSelector';

/**
 * SECTION: PLUGIN SELECTORS
 */

export const selectDataMap = createSelector([selectDetailProject], project =>
  project?.project_type === 'matterport' && project?.lat && project?.lng
    ? {
        markers: [
          {
            id: 'yX8lLEykClAEt9MollWiS',
            lat: project?.lat,
            lng: project?.lng,
          },
        ],
      }
    : onGetDetailPlugin(project?.plugins, 'map')?.dataMap || undefined,
);

export const selectConfigMap = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_MAP_CONFIG
      : onGetDetailPlugin(project?.plugins, 'map')?.configMap || undefined,
);

export const selectListFloorplan = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) =>
    isFreeOwnerPlan
      ? []
      : (
          onGetDetailPlugin(project?.plugins, 'floorplan')?.listFloorplan || []
        ).map(item => ({
          ...item,
          name: getTextMultiLang(
            item.name,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
        })),
);

export const selectConfigFloorplan = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_FLOORPLAN_CONFIG
      : onGetDetailPlugin(project?.plugins, 'floorplan')?.configFloorplan ||
        undefined,
);

export const selectConfigCollaboration = createSelector(
  [selectDetailProject],
  project =>
    onGetDetailPlugin(project?.plugins, 'collaboration')?.configCollaboration ||
    undefined,
);

const FREE_PLAN_HIDDEN_CONTROLBAR_KEYS = new Set<I_ControlbarKey>([
  'screenshot_capture',
  'floorplan',
  'multi_language',
  'map',
  'multi_staging',
]);

const CONTROLBAR_DEFAULT_KEYS = Object.keys(
  DEFAULT_VALUE_CONTROLBAR_CONFIG,
) as I_ControlbarKey[];

export const selectConfigControlbar = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) => {
    const config = onGetDetailPlugin(project?.plugins, 'controlbar')
      ?.configControlbar;
    if (!isFreeOwnerPlan) return config || undefined;

    const next = { ...config };
    const keysLen = CONTROLBAR_DEFAULT_KEYS.length;
    for (let i = 0; i < keysLen; i++) {
      const key = CONTROLBAR_DEFAULT_KEYS[i];
      const defaultItem = DEFAULT_VALUE_CONTROLBAR_CONFIG[key];
      next[key] = {
        isHide: FREE_PLAN_HIDDEN_CONTROLBAR_KEYS.has(key)
          ? true
          : config?.[key]?.isHide ?? defaultItem.isHide,
        type: 'icon',
        icon: defaultItem.icon,
      };
    }
    return next;
  },
);

export const selectConfigLogo = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_LOGO_CONFIG
      : onGetDetailPlugin(project?.plugins, 'logo')?.configLogo || undefined,
);

export const selectConfigScreenshotCapture = createSelector(
  [selectDetailProject],
  project =>
    onGetDetailPlugin(project?.plugins, 'screenshotCapture')
      ?.configScreenshotCapture || undefined,
);

export const selectConfigNadir = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? DEFAULT_VALUE_NADIR_CONFIG
      : onGetDetailPlugin(project?.plugins, 'nadir')?.configNadir || undefined,
);

export const selectConfigProtectPassword = createSelector(
  [selectDetailProject],
  project =>
    onGetDetailPlugin(project?.plugins, 'protectPassword')
      ?.configProtectPassword || undefined,
);

const PANOEE_BADGE_LINK = /^https?:\/\/(www\.)?panoee\.com\/?$/i;

const withBadgeUtm = <T extends I_CopyRightConfig>(
  config: T,
  tourId?: string,
): T => {
  if (!config.link || !PANOEE_BADGE_LINK.test(config.link)) return config;
  return {
    ...config,
    isPanoeeBadge: true,
    link: tourId
      ? `https://panoee.com/?utm_source=embed&utm_medium=badge&utm_campaign=made_with_panoee&utm_content=${encodeURIComponent(
          tourId,
        )}`
      : config.link,
  };
};

export const selectConfigCopyRight = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) => {
    const tourId = project?.id || project?.slug;
    if (isFreeOwnerPlan)
      return withBadgeUtm(DEFAULT_VALUE_COPYRIGHT_CONFIG, tourId);

    const config = onGetDetailPlugin(project?.plugins, 'copyRight')
      ?.configCopyRight;
    return config
      ? {
          ...withBadgeUtm(config, tourId),
          author: getTextMultiLang(
            config.author,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          description: getTextMultiLang(
            config.description,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
        }
      : undefined;
  },
);

export const selectConfigPopupIntro = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) => {
    if (isFreeOwnerPlan) return DEFAULT_VALUE_POPUP_INTRO_CONFIG;

    const config = onGetDetailPlugin(project?.plugins, 'popupIntro')
      ?.configPopupIntro;
    return config
      ? {
          ...config,
          textClose: getTextMultiLang(
            config.textClose,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
        }
      : undefined;
  },
);

export const selectListCallToAction = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) =>
    isFreeOwnerPlan
      ? []
      : (
          onGetDetailPlugin(project?.plugins, 'callToAction')
            ?.listCallToAction || []
        ).map(item => ({
          ...item,
          title: getTextMultiLang(
            item.title,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          value: getCtaValueMultiLang(
            item.value,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
        })),
);

// SECTION: PLUGIN FORM CAPTURE

export const selectListFormCapture = createSelector(
  [
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) =>
    isFreeOwnerPlan
      ? []
      : (
          onGetDetailPlugin(project?.plugins, 'formCapture')?.listFormCapture ||
          []
        ).map(item => ({
          ...item,
          title: getTextMultiLang(
            item.title,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          buttonTxt: getTextMultiLang(
            item.buttonTxt,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          description: getTextMultiLang(
            item.description,
            project?.keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          fields: item.fields.map(field => ({
            ...field,
            label: getTextMultiLang(
              field.label,
              project?.keyTranslations,
              languageProjectSelected,
              defaultLanguageProject,
            ),
          })),
        })),
);

export const selectConfigFormCapture = createSelector(
  [selectDetailProject, selectIsFreeOwnerPlan],
  (project, isFreeOwnerPlan) =>
    isFreeOwnerPlan
      ? []
      : onGetDetailPlugin(project?.plugins, 'formCapture')?.configFormCapture ||
        [],
);

export const selectHotspotSelectedTemporary = createSelector(
  [selectDomain],
  state => state.hotspotSelectedTemporary || null,
);

export const selectIsVisibleFormCapture = createSelector(
  [selectDomain],
  state => state.isVisibleFormCapture,
);

export const selectFormCaptureConfigSelected = createSelector(
  [selectDomain, selectIsFreeOwnerPlan],
  (state, isFreeOwnerPlan) =>
    isFreeOwnerPlan ? undefined : state.formCaptureConfigSelected || undefined,
);

export const selectFormCaptureSelected = createSelector(
  [selectListFormCapture, selectFormCaptureConfigSelected],
  (list, config) =>
    config && config.form
      ? list.filter(item => item.id === config?.form)[0] || undefined
      : undefined,
);

export const selectListSceneIdToOpenFormCapture = createSelector(
  [selectConfigFormCapture],
  config => onGetSceneIdsAttachedToFormCaptures(config),
);

export const selectListFormCaptureConfigCorrespondingToListSceneId =
  createSelector([selectConfigFormCapture], config =>
    onGetFormCapturesAttachedToSceneIds(config),
  );

export const selectListHotspotTypeToOpenFormCapture = createSelector(
  [selectConfigFormCapture],
  config => onGetHotspotTypesAttachedToFormCaptures(config),
);

export const selectListFormCaptureConfigCorrespondingToListHotspotType =
  createSelector([selectConfigFormCapture], config =>
    onGetFormCapturesAttachedToHotspotTypes(config),
  );

export const selectListHotspotIdToOpenFormCapture = createSelector(
  [selectListScene, selectConfigFormCapture],
  (listScene, config) =>
    onGetHotspotIdsAttachedToFormCaptures(listScene, config),
);

export const selectListFormCaptureConfigCorrespondingToListHotspotId =
  createSelector(
    [selectListScene, selectConfigFormCapture],
    (listScene, config) =>
      onGetFormCapturesAttachedToHotspotIds(listScene, config),
  );

// !SECTION: FORM CAPTURE
